/**
 * OD-40 (founder decision, REVISED 2026-10-15) — CORE-Y: imported schedulable tickets are placed through OptiPlace.
 *
 * The queue lists this shop's CSV-imported tickets still awaiting placement (unassigned, `awaiting-assignment`)
 * and opens the EXISTING OptiPlace recommendation for ONE ticket at a time (no stack of dialogs). OptiPlace ranks
 * the qualified technicians with the established rules and shows the top feasible technician, the date, the start
 * time and the explanation; the advisor accepts it, picks another valid technician / time, or closes it. "Defer"
 * leaves the ticket unassigned in the Parking Lot. Nothing here writes an assignment itself: the only write is
 * OptiPlace's accept (reassign-ticket, human-selected `optiplace-reassign`), which re-checks the placement on the
 * server (earliest feasible start, the technician's other work, compare-and-swap) — no double booking, no second
 * scheduling engine, and never a silent assignment.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { OptiPlaceModal } from '@/components/tickets/OptiPlaceModal';
import { supabase } from '@/integrations/supabase/client';
import { CSV_IMPORT_AWAITING_ASSIGNMENT_REASON } from '@/lib/import/csvImportRows';
import { getTodayInShopTz } from '@/utils/shopTime';

export interface ImportedTicketAwaitingPlacement {
  id: string;
  ticket_number: string | null;
  customer_name: string | null;
  vehicle_id: string | null;
  type: string | null;
  priority: string | null;
  is_comeback: boolean | null;
  predicted_minutes: number | null;
  scheduled_date: string | null;
}

const AWAITING_COLUMNS = 'id, ticket_number, customer_name, vehicle_id, type, priority, is_comeback, predicted_minutes, scheduled_date';

/** This shop's CSV-imported tickets still awaiting placement (unassigned, never deleted, still active work). */
async function readAwaitingPlacement(shopId: string): Promise<{ data: ImportedTicketAwaitingPlacement[] | null; error: { message: string } | null }> {
  // The generated table types make this filter chain exceed the compiler's instantiation depth (TS2589);
  // the row shape is declared above instead.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tickets = (supabase as any).from('tickets');
  return tickets
    .select(AWAITING_COLUMNS)
    .eq('shop_id', shopId)
    .eq('creation_method', 'csv_import')
    .eq('parking_lot_reason', CSV_IMPORT_AWAITING_ASSIGNMENT_REASON)
    .is('assigned_tech_id', null)
    .is('deleted_at', null)
    .eq('status', 'Active') // imported tickets are created Active (canonical status set)
    .order('scheduled_date', { ascending: true })
    .order('ticket_number', { ascending: true })
    .limit(500);
}

/** What a ticket lacks before OptiPlace can recommend for it (named, never filled in). Pure. */
export function placementMissingInformation(t: Pick<ImportedTicketAwaitingPlacement, 'predicted_minutes'>): string[] {
  const missing: string[] = [];
  if (!t.predicted_minutes || t.predicted_minutes <= 0) missing.push('job duration');
  return missing;
}

/** The day OptiPlace starts from: the imported day, unless it has already passed (then the shop's today). Pure. */
export function placementStartDay(scheduledDate: string | null, shopToday: string): string {
  return scheduledDate && scheduledDate >= shopToday ? scheduledDate : shopToday;
}

/** The next ticket to review: the first one neither deferred nor already reviewed in this pass. Pure. */
export function nextTicketToReview(
  tickets: ImportedTicketAwaitingPlacement[], deferred: ReadonlySet<string>,
): ImportedTicketAwaitingPlacement | null {
  return tickets.find((t) => !deferred.has(t.id) && placementMissingInformation(t).length === 0) ?? null;
}

export function ImportPlacementReviewQueue({ shopId, shopTimezone, refreshKey = 0 }: {
  shopId: string | null | undefined;
  shopTimezone: string;
  refreshKey?: number;
}) {
  const [tickets, setTickets] = useState<ImportedTicketAwaitingPlacement[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deferred, setDeferred] = useState<Set<string>>(() => new Set());
  const [reviewing, setReviewing] = useState<ImportedTicketAwaitingPlacement | null>(null);
  const [inSequence, setInSequence] = useState(false);

  const load = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    setLoadError(null);
    const { data, error } = await readAwaitingPlacement(shopId);
    setLoading(false);
    if (error) {
      // Failure is not absence: say the list could not be read rather than showing an empty queue.
      setLoadError(error.message);
      return;
    }
    setTickets(data ?? []);
  }, [shopId]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const pending = useMemo(() => tickets.filter((t) => !deferred.has(t.id)), [tickets, deferred]);
  const shopToday = getTodayInShopTz(shopTimezone);

  const openNext = useCallback((list: ImportedTicketAwaitingPlacement[], skip?: string) => {
    const next = nextTicketToReview(list.filter((t) => t.id !== skip), deferred);
    setReviewing(next);
    if (!next) setInSequence(false);
  }, [deferred]);

  const afterReview = useCallback(async (closedId: string, placed: boolean) => {
    setReviewing(null);
    const { data } = await readAwaitingPlacement(shopId ?? '');
    const list = data ?? tickets;
    setTickets(list);
    // Closing without accepting is not a decision: in a sequence it moves on to the next ticket; the closed one
    // stays awaiting placement (it was neither assigned nor deferred).
    if (inSequence) openNext(list, placed ? undefined : closedId);
  }, [inSequence, openNext, shopId, tickets]);

  if (!shopId) return null;
  if (!loading && !loadError && tickets.length === 0) return null;

  return (
    <Card className="mb-4" data-testid="import-placement-review">
      <CardHeader>
        <CardTitle>Imported tickets awaiting placement</CardTitle>
        <CardDescription>
          {loading ? 'Loading…' : `${pending.length} to place${deferred.size > 0 ? ` · ${deferred.size} deferred` : ''}. `}
          OptiPlace recommends a technician, date and start time for each; nothing is assigned until you accept.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {loadError && (
          <p className="text-sm text-destructive" role="alert" data-testid="import-placement-load-error">
            Could not read the imported tickets: {loadError}
          </p>
        )}
        {pending.length > 0 && (
          <Button
            size="sm"
            data-testid="import-placement-review-all"
            onClick={() => { setInSequence(true); openNext(tickets); }}
          >
            Review placements one by one
          </Button>
        )}
        <ul className="divide-y rounded-md border">
          {tickets.map((t) => {
            const missing = placementMissingInformation(t);
            const isDeferred = deferred.has(t.id);
            return (
              <li key={t.id} className="flex flex-wrap items-center gap-2 p-2 text-sm" data-testid={`import-placement-row-${t.id}`}>
                <span className="font-medium">{t.ticket_number || 'No RO number'}</span>
                <span className="text-muted-foreground">{t.customer_name || 'Customer not named'}</span>
                <span className="text-muted-foreground">{t.scheduled_date ?? ''}</span>
                {missing.length > 0 && (
                  <Badge variant="destructive" data-testid={`import-placement-missing-${t.id}`}>Missing: {missing.join(', ')}</Badge>
                )}
                {isDeferred && <Badge variant="secondary">Deferred — unassigned in the Parking Lot</Badge>}
                <span className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={missing.length > 0 || reviewing !== null}
                    data-testid={`import-placement-review-${t.id}`}
                    onClick={() => { setInSequence(false); setReviewing(t); }}
                  >
                    Review with OptiPlace
                  </Button>
                  {!isDeferred && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`import-placement-defer-${t.id}`}
                      onClick={() => setDeferred((prev) => new Set(prev).add(t.id))}
                    >
                      Defer
                    </Button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
        {reviewing && (
          <OptiPlaceModal
            isOpen
            onClose={() => void afterReview(reviewing.id, false)}
            onAssignmentComplete={() => void afterReview(reviewing.id, true)}
            ticketData={{
              id: reviewing.id,
              vehicleId: reviewing.vehicle_id ?? undefined,
              customerName: reviewing.customer_name ?? '',
              type: reviewing.type || 'Drop-off',
              priority: reviewing.priority || 'Normal',
              appointmentDate: `${placementStartDay(reviewing.scheduled_date, shopToday)}T12:00:00`,
              isComeback: reviewing.is_comeback === true,
              predictedMinutes: reviewing.predicted_minutes ?? 0,
              jobs: [], // OptiPlace reads the ticket's real job lines (useCanonicalSourceJobLines)
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
